import AvWorkforceLogo from 'src/components/AvWorkforceLogo';
import { selectedHeaderPalette } from 'src/theme/adminActionButton';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import {
  BriefcaseBusiness,
  Building2,
  ClipboardList,
  DollarSign,
  Ellipsis,
  History,
  LayoutDashboard,
  ShieldCheck,
  Users
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAdminWorkspace } from 'src/workspaces/AdminWorkspaceProvider';
import { useCompanyProfileWorkspaceScope } from 'src/workspaces/CompanyProfileWorkspaceScope';
import {
  companyWorkersPath,
  companyWorkspacePath
} from 'src/workspaces/tenantWorkspace';
import HeaderUserbox from './Userbox';
import { SidebarContext } from 'src/contexts/SidebarContext';

export const navItems = [
  {
    path: '/dashboards/home',
    label: 'Dashboard',
    icon: LayoutDashboard
  },
  {
    path: '/dashboards/jobTracker',
    label: 'Contracts',
    icon: BriefcaseBusiness
  },
  {
    path: '/dashboards/contractors',
    label: 'Workers',
    icon: Users
  },
  {
    path: '/dashboards/financials',
    label: 'Financials',
    icon: DollarSign
  },
  {
    path: '/dashboards/payroll',
    label: 'Payroll',
    icon: DollarSign
  }
  // Marketplace is intentionally absent: /dashboards/marketplace still renders
  // its "under development" placeholder for anyone holding the link, but a
  // primary nav item should not lead somewhere unbuilt (refs #1656, F12).
];

export const useWorkspaceHeaderNavigation = () => {
  const workspaceState = useAdminWorkspace();
  const profileWorkspace = useCompanyProfileWorkspaceScope();
  const companyProfilePath = profileWorkspace
    ? companyWorkspacePath(profileWorkspace, 'profile/overview')
    : null;
  const isPlatformCompany =
    workspaceState.kind === 'platform' &&
    profileWorkspace?.role === 'SUPER_ADMIN';
  const isCompanyWorkspace =
    workspaceState.kind === 'company' && workspaceState.status === 'ready';
  const companyDashboardPath = isCompanyWorkspace
    ? companyWorkspacePath(profileWorkspace!, 'dashboard')
    : null;
  const workersPath = isCompanyWorkspace
    ? companyWorkersPath(workspaceState.workspace)
    : null;
  const companyFinancialsPath = isCompanyWorkspace
    ? companyWorkspacePath(profileWorkspace!, 'financials')
    : null;
  const companyPayrollPath = isCompanyWorkspace
    ? companyWorkspacePath(profileWorkspace!, 'payroll')
    : null;
  const platformCompanyLinks = isPlatformCompany
    ? {
        workforce: `/platform/workforce?companyId=${profileWorkspace.company.id}`,
        clientProfiles: companyWorkspacePath(
          profileWorkspace,
          'profile/client-profiles'
        ),
        positions: companyWorkspacePath(profileWorkspace, 'profile/positions'),
        changeHistory: companyWorkspacePath(profileWorkspace, 'change-history')
      }
    : null;
  const headerNavItems = isPlatformCompany
    ? [
        {
          path: '/platform',
          label: 'Global Console',
          icon: ShieldCheck
        },
        {
          path: platformCompanyLinks!.workforce,
          label: 'Workforce',
          icon: Users
        },
        {
          path: companyProfilePath!,
          label: 'Company',
          icon: Building2
        },
        {
          path: platformCompanyLinks!.clientProfiles,
          label: 'Clients',
          icon: BriefcaseBusiness
        },
        {
          path: platformCompanyLinks!.positions,
          label: 'Positions',
          icon: ClipboardList
        },
        {
          path: platformCompanyLinks!.changeHistory,
          label: 'Change History',
          icon: History
        }
      ]
    : isCompanyWorkspace
    ? [
        {
          path: companyDashboardPath!,
          label: 'Dashboard',
          icon: LayoutDashboard
        },
        navItems[1],
        { ...navItems[2], path: workersPath! },
        { ...navItems[3], path: companyFinancialsPath! },
        { ...navItems[4], path: companyPayrollPath! }
      ]
    : navItems;

  return {
    companyProfilePath,
    headerNavItems,
    homePath: isPlatformCompany
      ? '/platform'
      : companyDashboardPath ?? '/dashboards/home',
    platformCompanyLinks,
    workspaceRole: isCompanyWorkspace
      ? workspaceState.workspace.accessLevel ?? workspaceState.workspace.role
      : workspaceState.kind === 'platform'
      ? workspaceState.principal.role
      : undefined
  };
};

const Header: React.FC = () => {
  const location = useLocation();
  const { sidebarToggle } = useContext(SidebarContext);
  const navSlot = useRef<HTMLDivElement>(null);
  const navContent = useRef<HTMLElement>(null);
  const [navigationFits, setNavigationFits] = useState(false);
  useLayoutEffect(() => {
    const slot = navSlot.current;
    const nav = navContent.current;
    if (!slot || !nav || typeof ResizeObserver === 'undefined') return;
    const measure = () => setNavigationFits(nav.getBoundingClientRect().width <= slot.clientWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    observer.observe(nav);
    measure();
    return () => observer.disconnect();
  }, []);
  const {
    companyProfilePath,
    headerNavItems,
    homePath,
    platformCompanyLinks,
    workspaceRole
  } = useWorkspaceHeaderNavigation();
  const showHeaderNavigation = navigationFits && !sidebarToggle;
  const primaryItemLimit =
    headerNavItems[0]?.label === 'Global Console' ? 4 : 6;
  const primaryItems = headerNavItems.slice(0, primaryItemLimit);
  const overflowItems = headerNavItems.slice(primaryItemLimit);
  const overflowActive = overflowItems.some((item) =>
    location.pathname.startsWith(item.path.split('?')[0])
  );

  return (
    <header style={{ '--selected-border': selectedHeaderPalette.border, '--selected-background': selectedHeaderPalette.backgroundImage, '--selected-text': selectedHeaderPalette.text } as React.CSSProperties} className="h-[82px] w-full border-b border-[#d5bd79]/10 bg-[#06070A]/95 text-white backdrop-blur">
      <div className="flex h-full items-center justify-between gap-6 px-4 sm:px-6 xl:px-8">
        <Link
          to={homePath}
          className="flex shrink-0 items-center rounded-2xl px-2 py-1"
        >
          <AvWorkforceLogo width={78} height={54} />
        </Link>

        <div ref={navSlot} className="relative min-w-0 flex-1">
        <nav ref={navContent} aria-hidden={!showHeaderNavigation} style={{ width: 'max-content', visibility: showHeaderNavigation ? 'visible' : 'hidden' }} className="relative flex items-center gap-1 overflow-visible rounded-[20px] border border-white/5 bg-white/[0.03] px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          {primaryItems.map((item) => {
            const isActive = location.pathname.startsWith(
              item.path.split('?')[0]
            );
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex shrink-0 items-center gap-2 rounded-2xl px-4 py-2 text-[15px] transition-all duration-200 ${
                  isActive
                    ? 'border border-[var(--selected-border)] bg-[image:var(--selected-background)] text-[var(--selected-text)] shadow-[0_10px_26px_rgba(232,191,33,0.12)]'
                    : 'text-white/[0.82] hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon
                  size={16}
                  className={isActive ? 'text-[#efc25c]' : 'text-white/[0.68]'}
                />
                <span className="font-medium tracking-[0.01em]">
                  {item.label}
                </span>
              </Link>
            );
          })}
          {overflowItems.length > 0 && (
            <details className="group/more relative shrink-0">
              <summary
                className={`flex cursor-pointer list-none items-center gap-2 rounded-2xl px-4 py-2 text-[15px] transition-all duration-200 [&::-webkit-details-marker]:hidden ${
                  overflowActive
                    ? 'border border-[var(--selected-border)] bg-[image:var(--selected-background)] text-[var(--selected-text)]'
                    : 'text-white/[0.82] hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Ellipsis size={17} aria-hidden="true" />
                <span className="font-medium">More</span>
              </summary>
              <div className="absolute right-0 top-[calc(100%+12px)] z-50 min-w-[220px] rounded-2xl border border-white/10 bg-[#111317] p-2 shadow-2xl">
                {overflowItems.map((item) => {
                  const isActive = location.pathname.startsWith(
                    item.path.split('?')[0]
                  );
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                        isActive
                          ? 'bg-[#f2d675] text-[#201b0b]'
                          : 'text-white/80 hover:bg-white/[0.07] hover:text-white'
                      }`}
                    >
                      <Icon size={16} aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </details>
          )}
        </nav>
        </div>

        <HeaderUserbox
          showNavigationMenu={!showHeaderNavigation}
          companyProfilePath={companyProfilePath}
          platformCompanyLinks={platformCompanyLinks}
          workspaceRole={workspaceRole}
        />
      </div>
    </header>
  );
};

export default Header;
